import type {
  MasterData,
  MasterDataVariables,
} from '@core/plugins/master-data/generated/master-data'
import QUERY from '@core/plugins/master-data/master-data.graphql'
import type { Apollo } from '@core/plugins/apollo/types'

export default defineNuxtPlugin({
  name: 'master-data',
  dependsOn: ['language', 'apollo'],
  parallel: true,
  async setup({ $apollo, $lang }) {
    try {
      const { data, error } = await useAsyncData<MasterData>(
        `masterData:${$lang as string}`,
        async () => {
          const { data, errors } = await ($apollo as Apollo).query<MasterData, MasterDataVariables>(
            {
              query: QUERY,
              variables: { lang: $lang as string },
            },
          )
          if (errors) {
            throw createGraphQLError(errors, 'Failed to load master data')
          }
          return {
            countries: [...data.countries].sort((a, b) =>
              a.labels.label.localeCompare(b.labels.label),
            ),
            currencies: [...data.currencies].sort((a, b) => a.iso.localeCompare(b.iso)),
          }
        },
      )

      if (error.value) {
        throw error.value
      }
      if (!data.value?.countries.length || !data.value.currencies.length) {
        throw new Error('Empty master data')
      }

      const countryIsoMap = new Map(data.value.countries.map((c) => [c.iso, c]))
      const countrySlugIsoMap = new Map(data.value.countries.map((c) => [c.slug, c.iso]))
      const currencyIsoMap = new Map(data.value.currencies.map((c) => [c.iso, c]))

      return {
        provide: {
          masterData: {
            countries: countryIsoMap,
            countrySlugIsoMap,
            currencies: currencyIsoMap,
          },
        },
      }
    } catch (e) {
      useLogger('plugin:master-data').error(e)
      throw e
    }
  },
})
